(() => {
  'use strict';
  const number = value => value === null || value === '' || value === undefined ? null : Number(value);
  function profile(input) {
    const name = String(input.name || '').trim();
    const length = number(input.length_mm);
    const width = number(input.width_mm);
    if (!name || name.length > 60 || !Number.isFinite(length) || length <= 0 || length > 500) throw new Error('invalid_profile');
    if (width !== null && (!Number.isFinite(width) || width <= 0 || width > 250)) throw new Error('invalid_profile');
    if (!input.id || typeof input.id !== 'string' || input.id.length > 100) throw new Error('invalid_profile');
    return {...input, name, length_mm: length, width_mm: width};
  }
  function readProfiles(raw) {
    const data = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(data) || data.length > 20) throw new Error('invalid_profiles');
    const profiles = data.map(profile);
    if (new Set(profiles.map(p => p.id)).size !== profiles.length) throw new Error('invalid_profiles');
    return profiles;
  }
  function matchVariant(variant, length, width, reserveMin = 5, reserveMax = 7) {
    if (!variant.available || !Number.isFinite(length) || length <= 0) return null;
    const minimum = number(variant.foot_min), maximum = number(variant.foot_max);
    const inner = number(variant.inner_length), innerWidth = number(variant.inner_width);
    let clearance = null, basis;
    if (Number.isFinite(minimum) && Number.isFinite(maximum) && minimum > 0 && maximum >= minimum) {
      if (length < minimum || length > maximum) return null;
      basis = 'foot_range';
      if (Number.isFinite(inner) && inner > length) clearance = inner - length;
    } else if (variant.percent_rule && Number.isFinite(inner) && inner > 0) {
      if (reserveMin < 0 || reserveMax < reserveMin) return null;
      clearance = inner - length;
      if (clearance + 0.00001 < length * reserveMin / 100 || clearance - 0.00001 > length * reserveMax / 100) return null;
      basis = 'inner_length';
    } else return null;
    if (Number.isFinite(width) && width > 0 && Number.isFinite(innerWidth) && innerWidth < width) return null;
    return {...variant, clearance, basis, width_checked: Number.isFinite(width) && Number.isFinite(innerWidth)};
  }
  const core = {profile, readProfiles, matchVariant};
  if (typeof module !== 'undefined' && module.exports) { module.exports = core; return; }
  if (customElements.get('sajust-foot-tools')) return;
  const query = 'query SajustProfileData { customer { id profiles: metafield(namespace: "custom", key: "foot_profiles") { value compareDigest } active: metafield(namespace: "custom", key: "active_foot_profile_id") { value compareDigest } } }';
  const mutation = 'mutation SaveSajustProfileData($metafields: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $metafields) { metafields { key value compareDigest } userErrors { field message code } } }';
  class FootTools extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.words = this.config.words;
      const currentURL = new URL(location.href);
      const preview = currentURL.searchParams.get('preview_theme_id');
      const alternate = currentURL.searchParams.get('view');
      if (preview || alternate === 'sajust-foot-profiles' || alternate === 'sajust-foot-finder') {
        const isAlternate = alternate === 'sajust-foot-profiles' || alternate === 'sajust-foot-finder';
        const profileURL = new URL(isAlternate ? currentURL.pathname : this.config.preview_path, location.origin);
        profileURL.search = new URLSearchParams({view: 'sajust-foot-profiles'});
        if (preview) profileURL.searchParams.set('preview_theme_id', preview);
        const finderURL = new URL(profileURL); finderURL.searchParams.set('view', 'sajust-foot-finder');
        this.config.profile_path = profileURL.pathname + profileURL.search;
        this.config.finder_path = finderURL.pathname + finderURL.search;
        this.querySelectorAll('[data-profile-link]').forEach(link => link.href = this.config.profile_path);
        this.querySelectorAll('[data-finder-link]').forEach(link => link.href = this.config.finder_path);
      }
      this.profiles = [];
      this.active = 'none';
      this.digests = {foot_profiles: null, active_foot_profile_id: null};
      this.key = `sajust-foot:${this.config.shop_id}:${this.config.client_id}`;
      this.controls = new AbortController();
      this.addEventListener('click', event => this.click(event), {signal: this.controls.signal});
      this.addEventListener('submit', event => this.submit(event), {signal: this.controls.signal});
      this.querySelector('[data-selector]')?.addEventListener('change', event => this.selectProfile(event.target.value), {signal: this.controls.signal});
      this.initialize().catch(() => this.message('account_error'));
    }
    disconnectedCallback() { this.controls?.abort(); }
    $(selector) { return this.querySelector(selector); }
    text(key, values = {}) {
      let text = this.words[key] || key;
      for (const [name, value] of Object.entries(values)) text = text.replaceAll(`{${name}}`, String(value));
      return text;
    }
    message(key, values = {}) {
      const output = this.$('[data-status]');
      output.textContent = this.text(key, values);
      output.hidden = false;
    }
    busy(value) {
      this.saving = value;
      this.toggleAttribute('aria-busy', value);
      this.querySelectorAll('[data-save], [data-delete-confirm], [data-login], [data-find]').forEach(button => button.disabled = value || (button.hasAttribute('data-login') && !this.config.client_id));
    }
    stored(name) { try { return JSON.parse(sessionStorage.getItem(`${this.key}:${name}`)); } catch { return null; } }
    store(name, value) { sessionStorage.setItem(`${this.key}:${name}`, JSON.stringify(value)); }
    remove(name) { sessionStorage.removeItem(`${this.key}:${name}`); }
    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {...options, signal: AbortSignal.timeout(15000)});
      if (!response.ok) throw new Error(response.status === 401 ? 'expired' : 'request_failed');
      return response.json();
    }
    async discover() {
      if (this.discovery) return this.discovery;
      const [auth, api] = await Promise.all([
        this.fetchJSON('/.well-known/openid-configuration'),
        this.fetchJSON('/.well-known/customer-account-api')
      ]);
      for (const endpoint of [auth.authorization_endpoint, auth.token_endpoint, api.graphql_api]) {
        if (!endpoint || new URL(endpoint).protocol !== 'https:') throw new Error('configuration');
      }
      this.discovery = {auth, api};
      return this.discovery;
    }
    async initialize() {
      if (!this.config.client_id) {
        this.$('[data-login]').disabled = true;
        this.$('[data-auth-text]').textContent = this.text('account_unavailable');
        return;
      }
      const url = new URL(location.href);
      if (url.searchParams.has('code') || url.searchParams.has('error')) await this.completeLogin(url);
      const token = this.stored('token');
      if (token && token.expires > Date.now() + 30000) {
        this.token = token;
        try { await this.loadProfiles(); } catch { this.disconnect(); this.message('account_error'); }
      } else this.remove('token');
    }
    random() {
      const bytes = crypto.getRandomValues(new Uint8Array(48));
      return btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
    }
    async login() {
      if (!this.config.client_id) { this.message('account_unavailable'); return; }
      const {auth} = await this.discover();
      const verifier = this.random(), state = this.random();
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
      const challenge = btoa(String.fromCharCode(...new Uint8Array(digest))).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
      const callback = new URL(this.config.profile_path, location.origin);
      callback.searchParams.delete('preview_theme_id');
      const redirect = callback.href;
      this.store('authorization', {verifier, state, redirect, created: Date.now(), return_path: location.pathname});
      const url = new URL(auth.authorization_endpoint);
      url.search = new URLSearchParams({client_id: this.config.client_id, scope: 'openid email customer-account-api:full', response_type: 'code', redirect_uri: redirect, state, code_challenge: challenge, code_challenge_method: 'S256', locale: this.config.locale}).toString();
      location.assign(url.toString());
    }
    async completeLogin(url) {
      const saved = this.stored('authorization');
      const code = url.searchParams.get('code'), state = url.searchParams.get('state'), error = url.searchParams.get('error');
      for (const name of ['code', 'state', 'error', 'error_description']) url.searchParams.delete(name);
      history.replaceState({}, '', url.pathname + url.search + url.hash);
      this.remove('authorization');
      if (error || !code || !saved || state !== saved.state || Date.now() - saved.created > 600000 || Date.now() < saved.created) throw new Error('authorization');
      const {auth} = await this.discover();
      const result = await this.fetchJSON(auth.token_endpoint, {method: 'POST', headers: {'Content-Type': 'application/x-www-form-urlencoded'}, body: new URLSearchParams({grant_type: 'authorization_code', client_id: this.config.client_id, redirect_uri: saved.redirect, code, code_verifier: saved.verifier}).toString()});
      if (!result.access_token || !Number.isFinite(Number(result.expires_in))) throw new Error('authorization');
      this.store('token', {access: result.access_token, expires: Date.now() + Number(result.expires_in) * 1000});
    }
    async customerAPI(query, variables = {}) {
      if (!this.token || this.token.expires <= Date.now() + 15000) { this.disconnect(); throw new Error('expired'); }
      const {api} = await this.discover();
      const result = await this.fetchJSON(api.graphql_api, {method: 'POST', headers: {'Content-Type': 'application/json', Authorization: this.token.access}, body: JSON.stringify({query, variables})});
      if (result.errors?.length) throw new Error('account_error');
      return result.data;
    }
    async loadProfiles() {
      const data = await this.customerAPI(query);
      if (!data.customer?.id) throw new Error('account_error');
      const customer = data.customer;
      const profiles = readProfiles(customer.profiles?.value);
      this.customerId = customer.id;
      this.profiles = profiles;
      this.active = profiles.some(p => p.id === customer.active?.value) ? customer.active.value : 'none';
      this.digests = {foot_profiles: customer.profiles?.compareDigest || null, active_foot_profile_id: customer.active?.compareDigest || null};
      this.renderProfiles();
      this.$('[data-auth]').hidden = true;
      this.$('[data-account-actions]').hidden = false;
      this.$('[data-save]').disabled = false;
      this.$('[data-profile-selector]').hidden = !this.profiles.length;
      if (this.active !== 'none') this.fillSearch(this.profiles.find(p => p.id === this.active));
    }
    disconnect() {
      this.remove('token');
      this.remove('authorization');
      this.token = null;
      this.customerId = null;
      this.profiles = [];
      this.active = 'none';
      this.$('[data-auth]').hidden = false;
      this.$('[data-account-actions]').hidden = true;
      this.$('[data-profile-selector]').hidden = true;
      this.renderProfiles();
      this.resetForm();
      this.$('[data-length]').value = '';
      this.$('[data-width]').value = '';
      this.$('[data-results]').replaceChildren();
      this.message('signed_out');
    }
    renderProfiles() {
      const container = this.$('[data-profiles]');
      container.replaceChildren();
      this.$('[data-empty]').hidden = this.profiles.length > 0;
      const selector = this.$('[data-selector]');
      selector.replaceChildren(new Option(this.text('manual_entry'), 'none'));
      for (const p of this.profiles) {
        selector.add(new Option(`${p.name} · ${p.length_mm} mm`, p.id));
        const card = document.createElement('article');
        card.className = 'sf-profile';
        const name = document.createElement('h3'); name.textContent = p.name;
        const length = document.createElement('p'); length.textContent = this.text('profile_measurements', {length: p.length_mm});
        const width = document.createElement('p'); width.textContent = p.width_mm === null ? this.text('width_not_set') : this.text('profile_width', {width: p.width_mm});
        card.append(name, length, width);
        if (p.measured_at && /^\d{4}-\d{2}-\d{2}$/.test(p.measured_at)) {
          const time = document.createElement('p'); time.className = 'sf-muted'; time.textContent = this.text('measured_at', {date: new Intl.DateTimeFormat(this.config.locale === 'en' ? 'en-GB' : this.config.locale).format(new Date(p.measured_at + 'T12:00:00'))}); card.append(time);
        }
        const actions = document.createElement('div'); actions.className = 'sf-actions';
        for (const [action, label] of [['use', 'use_profile'], ['edit', 'edit'], ['delete', 'delete']]) {
          const button = document.createElement('button'); button.type = 'button'; button.className = 'sf-link-button'; button.dataset[action] = p.id; button.textContent = this.text(label); actions.append(button);
        }
        card.append(actions); container.append(card);
      }
      selector.value = this.active;
    }
    async persist(profiles, active) {
      if (!this.customerId) throw new Error('sign_in_first');
      const checked = readProfiles(JSON.stringify(profiles));
      if (active !== 'none' && !checked.some(p => p.id === active)) throw new Error('invalid_profiles');
      const metafields = [
        {namespace: 'custom', key: 'foot_profiles', ownerId: this.customerId, type: 'json', value: JSON.stringify(checked), compareDigest: this.digests.foot_profiles},
        {namespace: 'custom', key: 'active_foot_profile_id', ownerId: this.customerId, type: 'single_line_text_field', value: active, compareDigest: this.digests.active_foot_profile_id}
      ];
      const data = await this.customerAPI(mutation, {metafields});
      const errors = data.metafieldsSet?.userErrors || [];
      if (errors.length) {
        if (errors.some(e => /digest/i.test(e.code || '') || /compare/i.test(e.message || ''))) {
          await this.loadProfiles(); throw new Error('conflict');
        }
        throw new Error('save_failed');
      }
      if (!data.metafieldsSet?.metafields || data.metafieldsSet.metafields.length !== 2) throw new Error('save_failed');
      for (const field of data.metafieldsSet.metafields) this.digests[field.key] = field.compareDigest;
      this.profiles = checked;
      this.active = active;
      this.renderProfiles();
      this.$('[data-profile-selector]').hidden = !checked.length;
    }
    resetForm() {
      this.editing = null;
      this.$('[data-profile-form]').reset();
      this.$('[data-form-title]').textContent = this.text('add_profile');
      this.$('[data-cancel]').hidden = true;
    }
    fillSearch(p) {
      if (!p) return;
      this.$('[data-length]').value = p.length_mm;
      this.$('[data-width]').value = p.width_mm ?? '';
    }
    async selectProfile(id) {
      const p = this.profiles.find(p => p.id === id);
      if (p) this.fillSearch(p);
      else { this.$('[data-length]').value = ''; this.$('[data-width]').value = ''; }
      if (this.customerId && id !== this.active) {
        this.busy(true);
        try { await this.persist(this.profiles, p?.id || 'none'); }
        catch (error) { this.message(error.message === 'conflict' ? 'conflict' : 'save_failed'); return false; }
        finally { this.busy(false); }
      }
      return true;
    }
    async click(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || this.saving) return;
      if (button.hasAttribute('data-login')) {
        this.busy(true);
        try { await this.login(); } catch { this.message('account_error'); this.busy(false); }
      } else if (button.hasAttribute('data-disconnect')) this.disconnect();
      else if (button.hasAttribute('data-cancel')) this.resetForm();
      else if (button.dataset.edit) {
        const p = this.profiles.find(p => p.id === button.dataset.edit);
        this.editing = p.id;
        const form = this.$('[data-profile-form]');
        form.elements.name.value = p.name; form.elements.length_mm.value = p.length_mm; form.elements.width_mm.value = p.width_mm ?? '';
        this.$('[data-form-title]').textContent = this.text('edit_profile');
        this.$('[data-cancel]').hidden = false;
        form.elements.name.focus();
      } else if (button.dataset.use) {
        const selected = await this.selectProfile(button.dataset.use);
        if (selected && this.config.mode === 'profiles') location.assign(this.config.finder_path);
      } else if (button.dataset.delete) {
        this.deleting = button.dataset.delete;
        this.$('[data-delete-dialog]').showModal();
      } else if (button.hasAttribute('data-delete-confirm')) {
        this.busy(true);
        try {
          const profiles = this.profiles.filter(p => p.id !== this.deleting);
          await this.persist(profiles, this.active === this.deleting ? 'none' : this.active);
          this.$('[data-delete-dialog]').close(); this.resetForm(); this.message('deleted');
        } catch (error) { this.message(error.message === 'conflict' ? 'conflict' : 'save_failed'); }
        finally { this.busy(false); }
      } else if (button.hasAttribute('data-delete-cancel')) this.$('[data-delete-dialog]').close();
    }
    async submit(event) {
      if (!event.target.matches('[data-profile-form], [data-find-form]')) return;
      event.preventDefault();
      if (this.saving) return;
      if (event.target.hasAttribute('data-find-form')) {
        this.busy(true);
        try { await this.findShoes(); } catch { this.message('catalogue_error'); }
        finally { this.busy(false); }
        return;
      }
      if (!this.customerId) { this.message(this.config.client_id ? 'sign_in_first' : 'account_unavailable'); return; }
      this.busy(true);
      try {
        if (!this.editing && this.profiles.length >= 20) throw new Error('profile_limit');
        const form = event.target, old = this.profiles.find(p => p.id === this.editing);
        const next = profile({...(old || {}), id: this.editing || crypto.randomUUID(), name: form.elements.name.value, length_mm: form.elements.length_mm.value, width_mm: form.elements.width_mm.value});
        const today = new Date();
        const measured = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        next.measured_at = old && old.length_mm === next.length_mm && old.width_mm === next.width_mm ? old.measured_at : measured;
        const profiles = old ? this.profiles.map(p => p.id === old.id ? next : p) : [...this.profiles, next];
        await this.persist(profiles, this.active === 'none' ? next.id : this.active);
        this.resetForm(); this.message('saved');
      } catch (error) { this.message(['invalid_profile','profile_limit','conflict'].includes(error.message) ? error.message : 'save_failed'); }
      finally { this.busy(false); }
    }
    async catalogue() {
      if (this.catalogueCache && Date.now() - this.catalogueCache.created < 30000) return this.catalogueCache.products;
      const url = new URL(this.config.catalogue_path, location.origin);
      url.searchParams.set('view', 'sajust-sizing-data');
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const first = await this.fetchJSON(url.toString(), {cache: 'no-store'});
      if (!Array.isArray(first.products) || !Number.isInteger(first.pages) || first.pages < 1 || first.pages > 100) throw new Error('catalogue_error');
      const products = [...first.products];
      for (let start = 2; start <= first.pages; start += 3) {
        const batch = await Promise.all(Array.from({length: Math.min(3, first.pages - start + 1)}, (_, i) => {
          const page = new URL(url); page.searchParams.set('page', String(start + i)); return this.fetchJSON(page.toString(), {cache: 'no-store'});
        }));
        for (const data of batch) { if (!Array.isArray(data.products)) throw new Error('catalogue_error'); products.push(...data.products); }
      }
      this.catalogueCache = {created: Date.now(), products};
      return products;
    }
    async findShoes() {
      const length = number(this.$('[data-length]').value), width = number(this.$('[data-width]').value);
      if (!Number.isFinite(length) || length <= 0 || length > 500 || (width !== null && (!Number.isFinite(width) || width <= 0 || width > 250))) { this.message('invalid_measurements'); return; }
      this.message('loading');
      const products = await this.catalogue();
      const results = products.map(p => ({...p, matches: p.variants.map(v => matchVariant(v, length, width, this.config.reserve_min, this.config.reserve_max)).filter(Boolean)})).filter(p => p.matches.length);
      const output = this.$('[data-results]'); output.replaceChildren();
      const seen = new Set();
      for (const p of results) {
        if (seen.has(p.id)) continue; seen.add(p.id);
        const card = document.createElement('article'); card.className = 'sf-product';
        if (p.image) {
          const image = document.createElement('img'); image.src = p.image; image.alt = p.title; image.width = 400; image.height = 400; image.loading = output.children.length < 3 ? 'eager' : 'lazy'; card.append(image);
        }
        const vendor = document.createElement('p'); vendor.className = 'sf-muted'; vendor.textContent = p.vendor;
        const heading = document.createElement('h3'); heading.textContent = p.title;
        card.append(vendor, heading);
        for (const v of p.matches) {
          const link = document.createElement('a');
          const target = new URL(p.url, location.origin);
          if (target.origin !== location.origin) continue;
          target.searchParams.set('variant', String(v.id));
          if (this.config.locale_root !== '/' && !target.pathname.startsWith(this.config.locale_root)) target.pathname = this.config.locale_root.replace(/\/$/, '') + target.pathname;
          const preview = new URL(location.href).searchParams.get('preview_theme_id'); if (preview) target.searchParams.set('preview_theme_id', preview);
          link.href = target.pathname + target.search; link.className = 'sf-size';
          const size = document.createElement('strong'); size.textContent = this.text('size', {size: v.size});
          const price = document.createElement('span'); price.textContent = v.price;
          const detail = document.createElement('span'); detail.className = 'sf-size-detail';
          detail.textContent = v.clearance !== null ? this.text('clearance', {length: v.inner_length, reserve: Math.round(v.clearance * 10) / 10}) : this.text('foot_range', {min: v.foot_min, max: v.foot_max});
          link.append(size, price, detail); card.append(link);
          if (width !== null) { const note = document.createElement('p'); note.className = 'sf-muted'; note.textContent = this.text(v.width_checked ? 'width_measured' : 'width_unknown'); card.append(note); }
        }
        output.append(card);
      }
      this.message(seen.size ? 'results_count' : 'no_results', {count: seen.size});
      this.$('[data-results-note]').hidden = false;
    }
  }
  customElements.define('sajust-foot-tools', FootTools);
})();

